import type { RouteLocationNormalizedLoaded } from 'vue-router';
import { pageTabRouteKey } from '@/utils/pageTabRouteKey';
import { projectDashboardTabKey } from '@/utils/projectPageTab';

export const PROJECT_AI_ROUTE_NAMES = new Set([
  'ProjectDashboard',
  'ProjectRequirements',
  'ProjectDocumentViewer',
  'ProjectWbsSnapshot',
]);

export function isProjectAiRoute(route: RouteLocationNormalizedLoaded): boolean {
  const name = String(route.name ?? '');
  return PROJECT_AI_ROUTE_NAMES.has(name)
    || (typeof route.path === 'string' && /^\/project\/[^/]+\//.test(route.path));
}

export function resolveProjectIdFromRoute(route: RouteLocationNormalizedLoaded): number {
  // 仅项目路由下解析；避免 AI 需求详情等页的 params.id（需求 ID）被当成项目 ID
  if (!isProjectAiRoute(route)) return 0;
  const rawId = route.params.id ?? route.params.projectId ?? route.params.pid;
  if (rawId == null || rawId === '') return 0;
  const value = Number(Array.isArray(rawId) ? rawId[0] : rawId);
  return Number.isFinite(value) && value > 0 ? value : 0;
}

/** 智能文档独立会话场景前缀（与项目空间会话隔离） */
export const SMART_DOCUMENT_SCENARIO_PREFIX = 'smart_document_';

const SMART_DOCUMENT_ROUTE_NAMES = new Set([
  'SmartDocumentWorkbench',
  'SmartDocumentWorkbenchHome',
]);

/** 智能文档工作台会话场景：个人 / 项目工作区各自独立历史 */
export function resolveSmartDocumentScenario(workspaceId: number | string): string {
  const id = String(workspaceId ?? '0');
  if (!id || id === '0') {
    return `${SMART_DOCUMENT_SCENARIO_PREFIX}personal`;
  }
  return `${SMART_DOCUMENT_SCENARIO_PREFIX}ws_${sanitizeContextPart(id)}`;
}

export function isSmartDocumentScenario(scenario: string | null | undefined): boolean {
  return !!scenario && scenario.startsWith(SMART_DOCUMENT_SCENARIO_PREFIX);
}

function sanitizeContextPart(raw: string): string {
  return String(raw ?? '')
    .replace(/[^a-zA-Z0-9:_-]/g, '_')
    .slice(0, 160);
}

/** 顶栏页签级页面上下文 key，用于判断是否需要切换 AI 会话 */
export function resolveAiPageContextKey(route: RouteLocationNormalizedLoaded): string {
  const name = route.name != null ? String(route.name) : '';
  // 文档评审 / WBS 已并入项目内 Tab：与项目详情共用同一 AI 会话上下文
  if (name === 'ProjectDocumentViewer' || name === 'ProjectWbsSnapshot' || name === 'ProjectDashboard') {
    const rawId = route.params.id;
    const pid = Array.isArray(rawId) ? rawId[0] : rawId;
    if (pid != null && String(pid) !== '') {
      return projectDashboardTabKey(String(pid));
    }
  }
  return pageTabRouteKey({
    name: name || undefined,
    params: route.params as Record<string, unknown>,
    query: route.query as Record<string, unknown>,
  });
}

/** 项目空间 AI 会话场景（与项目详情页 resolveAiConversationScenario 一致） */
export function resolveProjectSpaceScenario(projectId: number | string): string {
  const id = String(projectId);
  const ctxKey = sanitizeContextPart(projectDashboardTabKey(id));
  return `project_assistance_project_${id}_ctx_${ctxKey}`;
}

/**
 * 按「页面上下文」划分 AI 会话场景：同项目不同页面、AI 工具、知识空间等各自独立历史。
 */
export function resolveAiConversationScenario(
  route: RouteLocationNormalizedLoaded,
  fallbackScenario = 'project_assistance',
): string {
  const name = String(route.name ?? '');
  const ctxKey = sanitizeContextPart(resolveAiPageContextKey(route));

  if (name === 'Resources' || name === 'ResourceCreate' || name === 'ResourceDetail') {
    return 'component_library';
  }

  // 智能文档：独立会话，不与项目详情 / 文档评审等共用历史
  if (SMART_DOCUMENT_ROUTE_NAMES.has(name) || route.path === '/smart-document'
      || (typeof route.path === 'string' && /\/smart-document\/?$/.test(route.path))) {
    const rawWs = route.params.id ?? route.params.projectId ?? route.params.pid;
    const workspaceId = rawWs == null || rawWs === ''
      ? 0
      : Number(Array.isArray(rawWs) ? rawWs[0] : rawWs);
    return resolveSmartDocumentScenario(
      Number.isNaN(workspaceId) ? 0 : workspaceId,
    );
  }

  // 创建项目页与项目列表共用通用场景，便于 AI 创建意图会话随导航转移
  if (name === 'ProjectCreate' || name === 'Dashboard') {
    return fallbackScenario.trim() || 'project_assistance';
  }

  const projectId = resolveProjectIdFromRoute(route);
  const inProjectPath = isProjectAiRoute(route);

  if (inProjectPath && projectId > 0) {
    return resolveProjectSpaceScenario(projectId);
  }

  if (ctxKey && ctxKey !== 'Dashboard') {
    return `page_ctx_${ctxKey}`;
  }

  return fallbackScenario.trim() || 'project_assistance';
}
